// src/predicates.ts
function isNode(el, state2) {
  return el instanceof HTMLElement && el.parentNode instanceof HTMLElement || state2.lastParent instanceof HTMLElement;
}

// src/state.ts
var state = {
  activeNode: void 0,
  enterCount: 0,
  clonedDraggedNodes: Array(),
  direction: void 0,
  draggedNode: void 0,
  draggedNodes: Array(),
  dropZones: /* @__PURE__ */ new WeakMap(),
  dropped: false,
  hiddenNodes: /* @__PURE__ */ new WeakMap(),
  initialParent: void 0,
  initialParentValues: [],
  lastCoordinates: {
    x: 0,
    y: 0
  },
  lastParent: void 0,
  lastValue: void 0,
  leftParent: false,
  longTouch: false,
  longTouchTimeout: void 0,
  nodeData: /* @__PURE__ */ new WeakMap(),
  parentData: /* @__PURE__ */ new WeakMap(),
  parentObservers: /* @__PURE__ */ new WeakMap(),
  parents: /* @__PURE__ */ new WeakSet(),
  preventEnter: false,
  removeDraggable: void 0,
  scrollParent: void 0,
  scrollParentOverflow: void 0,
  touchMoving: false,
  selectedNodes: Array(),
  selectedValues: Array(),
  touchEnded: false,
  touchedNode: void 0,
  touchStartLeft: void 0,
  touchStartTop: void 0
};

// src/utils.ts
var isBrowser = typeof window !== "undefined";
function cleanUp(e) {
  if (!state.draggedNode || !state.initialParent || !state.lastParent)
    return;
  const config = state.parentData.get(state.initialParent)?.config;
  const root = config?.root || document;
  if (config) {
    const hasSelections = state.selectedValues.length > 0;
    const isTouch = e.event instanceof TouchEvent;
    let dropZoneClass;
    if (isTouch) {
      dropZoneClass = hasSelections ? config.touchSelectionDropZoneClass : config.touchDropZoneClass;
    } else {
      dropZoneClass = hasSelections ? config.selectionDropZoneClass : config.dropZoneClass;
    }
    handleClass(state.draggedNodes, dropZoneClass, state, true);
    if (dropZoneClass) {
      const elsWithDropZoneClass = root.querySelectorAll(
        `.${splitClass(dropZoneClass)}`
      );
      handleClass(Array.from(elsWithDropZoneClass), dropZoneClass, state, true);
    }
  }
  handleClass(
    state.draggedNodes,
    state.parentData.get(state.initialParent)?.config?.longTouchClass,
    state,
    true
  );
  state.touchedNode?.remove();
  state.touchedNode = void 0;
  state.lastParent = void 0;
  state.draggedNode = void 0;
  state.draggedNodes = [];
  state.selectedNodes = [];
  state.selectedValues = [];
  state.touchStartTop = state.touchedNode = void 0;
  state.touchStartLeft = 0;
  state.lastValue = void 0;
  state.touchMoving = false;
  state.lastCoordinates = {
    x: 0,
    y: 0
  };
  state.direction = void 0;
  if (state.scrollParent) {
    state.scrollParent.style.overflow = state.scrollParentOverflow || "";
    state.scrollParent = void 0;
    state.scrollParentOverflow = void 0;
  }
}
function dragTransfer() {
  if (!state.draggedNode || !state.lastParent || !state.initialParent)
    return;
  const draggedNodeData = state.nodeData.get(state.draggedNode);
  const draggedParentData = state.parentData.get(state.initialParent);
  const lastParentData = state.parentData.get(state.lastParent);
  if (!draggedNodeData || !draggedParentData || !lastParentData)
    return;
  else
    return {
      draggedNode: state.draggedNode,
      draggedNodeData,
      draggedParent: state.initialParent,
      draggedParentData,
      lastParent: state.lastParent,
      lastParentData
    };
}
function touchTransfer() {
  if (!state.touchedNode)
    return;
  const data = dragTransfer();
  if (data === void 0)
    return;
  const transferTouchData = {
    ...data,
    touchedNode: state.touchedNode
  };
  return transferTouchData;
}
function nodeDragTarget(e) {
  if (!isNode(e.currentTarget, state))
    return;
  const targetNodeData = state.nodeData.get(e.currentTarget);
  const targetParentData = state.parentData.get(
    e.currentTarget.parentNode || state.lastParent
  );
  if (!targetNodeData || !targetParentData)
    return;
  const targetData = {
    targetNode: e.currentTarget,
    targetNodeData,
    targetParent: e.currentTarget.parentNode || state.lastParent,
    targetParentData
  };
  return targetData;
}
function dzDragTarget(e) {
  if (!isNode(e.currentTarget, state))
    return;
  const targetParentData = state.parentData.get(e.currentTarget);
  if (!targetParentData)
    return;
  const targetData = {
    targetParent: e.currentTarget,
    targetParentData
  };
  return targetData;
}
function getTouchTarget(e) {
  const newX = e.touches[0].clientX;
  const newY = e.touches[0].clientY;
  const nodes = document.elementsFromPoint(newX, newY);
  if (!nodes)
    return;
  for (const node of nodes) {
    if (isNode(node, state) && state.nodeData.has(node)) {
      const targetNode = node;
      const targetNodeData = state.nodeData.get(targetNode);
      const targetParentData = state.parentData.get(targetNode.parentNode);
      if (!targetNodeData || !targetParentData)
        return;
      return {
        targetNode,
        targetNodeData,
        targetParent: targetNode.parentNode,
        targetParentData
      };
    } else if (node instanceof HTMLElement && state.dropZones.has(node)) {
      if (!state.parentData.has(node))
        return;
      const targetParentData = state.parentData.get(node);
      if (!targetParentData)
        return;
      return {
        targetParent: node,
        targetParentData
      };
    }
  }
  return void 0;
}
function handleSelections(e, selectedValues, state2, x, y) {
  state2.selectedValues = selectedValues;
  for (const child of e.draggedParentData.enabledNodes) {
    if (child === e.draggedNode) {
      state2.selectedNodes.push(child);
      continue;
    }
    const childValue = state2.nodeData.get(child)?.value;
    if (!childValue)
      continue;
    if (selectedValues?.includes(childValue)) {
      state2.draggedNodes.push(child);
    }
  }
  const config = e.draggedParentData.config;
  const clonedEls = state2.draggedNodes.map((x2) => {
    const el = x2.cloneNode(true);
    copyNodeStyle(x2, el, true);
    if (e.event instanceof DragEvent)
      handleClass([el], config?.selectionDraggingClass, state2);
    return el;
  });
  setTimeout(() => {
    if (e.event instanceof DragEvent && config?.selectionDropZoneClass)
      for (const node of state2.draggedNodes) {
        node.classList.add(config?.selectionDropZoneClass);
      }
  });
  state2.clonedDraggedNodes = clonedEls;
  return { e, clonedEls, x, y };
}
function stackNodes({
  e,
  clonedEls,
  x,
  y
}) {
  const wrapper = document.createElement("div");
  if (!(wrapper instanceof HTMLElement))
    return;
  for (const el of clonedEls)
    wrapper.append(el);
  const rect = e.draggedNode.getBoundingClientRect();
  wrapper.style.cssText = `
        display: flex;
        flex-direction: column;
        width: ${rect.width}px;
        position: absolute;
        left: -9999px
      `;
  document.body.append(wrapper);
  if (e.event instanceof DragEvent) {
    e.event.dataTransfer?.setDragImage(wrapper, x, y);
    setTimeout(() => {
      wrapper.remove();
    });
  } else {
    state.touchedNode = wrapper;
  }
}
function copyNodeStyle(sourceNode, targetNode, omitKeys = false) {
  const computedStyle = window.getComputedStyle(sourceNode);
  const omittedKeys = [
    "position",
    "top",
    "left",
    "x",
    "y",
    "transform-origin",
    "filter",
    "-webkit-text-fill-color"
  ];
  for (const key of Array.from(computedStyle)) {
    if (omitKeys === false && key && omittedKeys.includes(key))
      continue;
    targetNode.style.setProperty(
      key,
      computedStyle.getPropertyValue(key),
      computedStyle.getPropertyPriority(key)
    );
  }
  for (const child of Array.from(sourceNode.children)) {
    if (!isNode(child, state))
      continue;
    const targetChild = targetNode.children[Array.from(sourceNode.children).indexOf(child)];
    copyNodeStyle(child, targetChild, omitKeys);
  }
}
function handleClass(nodes, className, state2, remove = false, omitAppendPrivateClass = false) {
  if (!className)
    return;
  const classNames = splitClass(className);
  if (!classNames.length)
    return;
  remove ? removeClass(nodes, classNames, state2) : addClass(nodes, classNames, state2, omitAppendPrivateClass);
}
function addClass(nodes, classNames, state2, omitAppendPrivateClass = false) {
  if (classNames.includes("longTouch"))
    return;
  for (const node of nodes) {
    if (!isNode(node, state2) || !state2.nodeData.has(node)) {
      node.classList.add(...classNames);
      continue;
    }
    const privateClasses = [];
    const nodeData = state2.nodeData.get(node);
    if (!nodeData)
      continue;
    for (const className of classNames) {
      if (!node.classList.contains(className)) {
        node.classList.add(className);
      } else if (node.classList.contains(className) && omitAppendPrivateClass === false) {
        privateClasses.push(className);
      }
    }
    nodeData.privateClasses = privateClasses;
    state2.nodeData.set(node, nodeData);
  }
}
function removeClass(nodes, classNames, state2) {
  for (const node of nodes) {
    if (!isNode(node, state2)) {
      node.classList.remove(...classNames);
      continue;
    }
    const nodeData = state2.nodeData.get(node);
    if (!nodeData)
      continue;
    for (const className of classNames) {
      if (!nodeData.privateClasses.includes(className)) {
        node.classList.remove(className);
      }
    }
  }
}
function splitClass(className) {
  return className.split(" ").filter((x) => x);
}
function getScrollParent(node) {
  if (node == null) {
    return void 0;
  }
  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    if (node.parentNode instanceof HTMLElement)
      return getScrollParent(node.parentNode);
  }
  return void 0;
}

// src/validators.ts
function validateSort(e) {
  const dzConfig = e.targetParentData.dzConfig;
  if (dzConfig && dzConfig.nodeDropZone === false)
    return false;
  return e.targetParentData.config?.sortable !== false;
}
function validateTransfer(e) {
  const draggedValues = state.draggedNodes.map(
    (el) => state.nodeData.get(el)?.value
  );
  const targetParentValues = e.targetParentData.getValues(e.targetParent);
  const draggedParentValues = e.draggedParentData.getValues(e.draggedParent);
  const ctData = {
    name: e.targetParentData.config?.name,
    group: e.targetParentData.dzConfig?.group,
    values: targetParentValues
  };
  const deData = {
    name: e.draggedParentData.config?.name,
    group: e.draggedParentData.dzConfig?.group,
    values: draggedParentValues
  };
  const accepts = e.targetParentData.dzConfig?.accepts;
  if (accepts)
    return accepts(ctData, deData, draggedValues);
  else if (ctData.group) {
    return ctData.group === deData.group;
  }
  return true;
}

// src/actions.ts
function dragstart(e, state2) {
  if (e.event.dataTransfer) {
    e.event.dataTransfer.dropEffect = "move";
    e.event.dataTransfer.effectAllowed = "move";
  }
  const config = e.targetParentData.config;
  const selectedValues = config?.setSelections && config?.setSelections(e.draggedNode.parentNode);
  if (Array.isArray(selectedValues) && selectedValues.length) {
    const targetRect = e.targetNode.getBoundingClientRect();
    const x = e.event.clientX - targetRect.left;
    const y = e.event.clientY - targetRect.top;
    stackNodes(handleSelections(e, selectedValues, state2, x, y));
  } else if (config) {
    handleClass(state2.draggedNodes, config.draggingClass, state2);
    setTimeout(() => {
      handleClass(state2.draggedNodes, config.draggingClass, state2, true);
      handleClass(state2.draggedNodes, config.dropZoneClass, state2);
    });
  }
}
function touchstart(e, state2) {
  e.event.stopPropagation();
  const rect = e.draggedNode.getBoundingClientRect();
  state2.touchStartLeft = e.event.touches[0].clientX - rect.left;
  state2.touchStartTop = e.event.touches[0].clientY - rect.top;
  const config = e.draggedParentData.config;
  const selectedValues = config?.setSelections && config?.setSelections(e.draggedNode.parentNode);
  if (Array.isArray(selectedValues) && selectedValues.length) {
    stackNodes(
      handleSelections(
        e,
        selectedValues,
        state2,
        state2.touchStartLeft,
        state2.touchStartTop
      )
    );
  } else {
    state2.touchedNode = e.touchedNode;
    e.touchedNode.style.cssText = `
            width: ${rect.width}px;
            position: absolute;
            pointer-events: none;
            top: -9999px;
            z-index: 999999;
            display: none;
          `;
    document.body.append(e.touchedNode);
    copyNodeStyle(e.draggedNode, e.touchedNode);
    e.touchedNode.style.display = "none";
  }
  if (config?.longTouch) {
    state2.longTouchTimeout = setTimeout(() => {
      state2.longTouch = true;
      const parentScroll = getScrollParent(e.draggedNode);
      if (parentScroll) {
        state2.scrollParent = parentScroll;
        state2.scrollParentOverflow = parentScroll.style.overflow;
        parentScroll.style.overflow = "hidden";
      }
      if (config.longTouchClass)
        handleClass(state2.draggedNodes, config.longTouchClass, state2);
      if (e.event.cancelable)
        e.event.preventDefault();
      document.addEventListener("contextmenu", function(e2) {
        e2.preventDefault();
      });
    }, 200);
  }
}
function touchmove(e, state2) {
  if (!state2.touchedNode)
    return;
  const config = e.draggedParentData.config;
  if (config?.longTouch && !state2.longTouch) {
    clearTimeout(state2.longTouchTimeout);
    return;
  }
  if (state2.touchMoving !== true) {
    state2.touchMoving = true;
    handleClass(state2.draggedNodes, config?.longTouchClass, state2, true);
    const hasSelections = Array.isArray(state2.selectedValues) && state2.selectedValues.length;
    if (hasSelections) {
      handleClass(
        state2.clonedDraggedNodes,
        config?.touchSelectionDraggingClass,
        state2
      );
      handleClass(
        state2.draggedNodes,
        config?.touchSelectionDropZoneClass,
        state2
      );
    } else {
      handleClass([state2.touchedNode], config?.touchDraggingClass, state2);
      handleClass(state2.draggedNodes, config?.touchDropZoneClass, state2);
    }
  }
  e.touchedNode.style.display = "block";
  const x = e.event.touches[0].clientX + window.scrollX;
  const y = e.event.touches[0].clientY + window.scrollY;
  const windowHeight = window.innerHeight + window.scrollY;
  if (y > windowHeight - 50) {
    window.scrollBy(0, 10);
  } else if (y < window.scrollY + 50) {
    window.scrollBy(0, -10);
  }
  const touchStartLeft = state2.touchStartLeft ?? 0;
  const touchStartTop = state2.touchStartTop ?? 0;
  e.touchedNode.style.left = `${x - touchStartLeft}px`;
  e.touchedNode.style.top = `${y - touchStartTop}px`;
  if (e.event.cancelable)
    e.event.preventDefault();
}
function sort(e, state2) {
  const targetParentValues = e.targetParentData.getValues(e.targetParent);
  const draggedNodeValues = state2.draggedNodes.map(
    (x) => state2.nodeData.get(x)?.value
  );
  const newParentValues = [
    ...targetParentValues.filter((x) => !draggedNodeValues.includes(x))
  ];
  newParentValues.splice(e.targetNodeData.index, 0, ...draggedNodeValues);
  e.targetParentData.setValues(e.targetParent, newParentValues);
}
function transferReturn(e, state2) {
  if (e.draggedParent === e.lastParent)
    return;
  const leftParentValues = [...e.lastParentData.getValues(e.lastParent)];
  const draggedValues = state2.draggedNodes.map(
    (x) => state2.nodeData.get(x)?.value
  );
  const newLeftParentValues = [
    ...leftParentValues.filter((x) => !draggedValues.includes(x))
  ];
  e.lastParentData.setValues(e.lastParent, newLeftParentValues);
  state2.lastParent = e.draggedParent;
  e.draggedParentData.setValues(e.draggedParent, state2.initialParentValues);
}
function dragleave(_e, _state) {
}
function end(e, state2) {
  if (e.event instanceof DragEvent && state2.longTouch)
    return;
  if (e.event instanceof TouchEvent) {
    state2.longTouch = false;
    clearTimeout(state2.longTouchTimeout);
  }
  if (state2.draggedNode && state2.draggedNode.parentNode && state2.draggedNode.parentNode !== state2.lastParent && state2.initialParent) {
    const deData = state2.nodeData.get(state2.draggedNode);
    if (deData) {
      const deParentData = state2.parentData.get(state2.draggedNode.parentNode);
      const deParentValues = [
        ...deParentData?.getValues(state2.draggedNode.parentNode) || []
      ];
      const deIndex = deData.index;
      deParentValues.splice(deIndex, state2.draggedNodes.length);
      deParentData?.setValues(state2.initialParent, deParentValues);
    }
  }
  cleanUp(e);
}
function drop(e, _state) {
  cleanUp(e);
}

// src/plugins/dropZone.ts
function setUpDropZone(dzConfig = {}, parent, config) {
  const parentData = state.parentData.get(parent);
  if (!parentData)
    return;
  !config.disabled ? activateDZ(parent, dzConfig, parent) : deactivateDZ(parent);
  for (const dz of dzConfig.validDropZones || []) {
    activateDZ(dz, dzConfig, parent);
  }
  const parentConfig = parentData.config;
  if (!parentConfig || !parentConfig.setDraggable)
    return;
  const setDraggable2 = parentConfig.setDraggable;
  parentConfig.setDraggable = (node) => {
    setDraggable2(node);
    setNodeEvents(node);
    return node;
  };
}
function activateDZ(dz, dzConfig, parent) {
  dz.addEventListener("dragover", dzDragEvent);
  dz.addEventListener("drop", dzDragEvent);
  const parentData = state.parentData.get(dz);
  if (!parentData)
    return;
  if (parent) {
    parentData.dzConfig = dzConfig;
    state.dropZones.set(dz, parent);
    state.parentData.set(dz, parentData);
  } else {
    parentData.dzConfig = dzConfig;
    state.parentData.set(dz, parentData);
  }
}
function deactivateDZ(dz) {
  dz.removeEventListener("dragover", dzDragEvent);
  dz.removeEventListener("drop", dzDragEvent);
  const parentData = state.parentData.get(dz);
  if (!parentData)
    return;
}
function setNodeEvents(node) {
  node.addEventListener("dragover", nodeDragEvent);
  node.addEventListener("touchmove", nodeTouchEvent);
}
function dzDragEvent(e) {
  e.stopPropagation();
  e.preventDefault();
  const targetData = dzDragTarget(e);
  if (targetData === void 0)
    return;
  const transferData = dragTransfer();
  if (transferData === void 0)
    return;
  const event = {
    event: e,
    ...targetData,
    ...transferData
  };
  const dzConfig = event.targetParentData.dzConfig;
  switch (event.event.type) {
    case "dragover":
      if (state.lastParent !== event.targetParent && event.draggedParent !== event.targetParent) {
        handleTransfer(event, state);
        state.enterCount++;
        state.leftParent = true;
      }
      break;
    case "drop":
      if (event.targetParent !== event.draggedParent) {
        dzConfig && dzConfig.drop ? dzConfig.drop(event, state, transferDrop) : transferDrop(event, state);
      }
  }
}
function nodeDragEvent(e) {
  e.stopPropagation();
  e.preventDefault();
  const targetData = nodeDragTarget(e);
  if (!targetData)
    return;
  const transferData = dragTransfer();
  if (transferData === void 0)
    return;
  const event = {
    event: e,
    ...targetData,
    ...transferData
  };
  if (!event)
    return;
  if (e.type === "dragover" && state.lastParent !== event.targetParent && event.targetParent !== event.draggedParent) {
    const dzConfig = event.targetParentData?.dzConfig;
    if (dzConfig && dzConfig.nodeDropZone === false)
      return;
    if (event.targetParent !== event.draggedParent) {
      handleTransfer(event, state);
      state.leftParent = true;
    }
  }
}
function nodeTouchEvent(e) {
  if (e.type !== "touchmove")
    return;
  const targetData = getTouchTarget(e);
  if (targetData === void 0)
    return;
  const transferData = touchTransfer();
  if (transferData === void 0)
    return;
  if ("targetNode" in targetData) {
    const nodeEvent = {
      event: e,
      ...targetData,
      ...transferData
    };
    if (nodeEvent.targetParent === nodeEvent.draggedParent) {
      transferReturn(nodeEvent, state);
      return;
    }
    if (nodeEvent.lastParent == nodeEvent.targetParent)
      return;
    handleTransfer(nodeEvent, state);
    state.leftParent = true;
  } else {
    const parentEvent = {
      event: e,
      ...targetData,
      ...transferData
    };
    if (parentEvent.lastParent !== parentEvent.draggedParent && parentEvent.draggedParent === parentEvent.targetParent) {
      transferReturn(parentEvent, state);
      state.leftParent = false;
    } else if (parentEvent.targetParent !== parentEvent.draggedParent && parentEvent.lastParent !== parentEvent.targetParent) {
      handleTransfer(parentEvent, state);
      state.leftParent = true;
    }
  }
}
function handleTransfer(e, state2) {
  if (!validateTransfer(e))
    return;
  const dzConfig = e.targetParentData.dzConfig;
  dzConfig?.transfer ? dzConfig.transfer(e, state2, transferAction) : transferAction(e, state2);
  state2.lastParent = e.targetParent;
}
function transferAction(e, state2) {
  let lastParentValues = [...e.lastParentData.getValues(e.lastParent)];
  const draggedNodeValues = state2.draggedNodes.map(
    (x) => state2.nodeData.get(x)?.value
  );
  lastParentValues = [
    ...lastParentValues.filter((x) => !draggedNodeValues.includes(x))
  ];
  const targetParentValues = [...e.targetParentData.getValues(e.targetParent)];
  if ("targetNodeData" in e) {
    targetParentValues.splice(
      e.targetNodeData.index,
      0,
      ...state2.draggedNodes.map((x) => state2.nodeData.get(x)?.value)
    );
  } else {
    targetParentValues.push(...draggedNodeValues);
  }
  e.lastParentData.setValues(e.lastParent, lastParentValues);
  e.targetParentData.setValues(e.targetParent, targetParentValues);
}
function transferDrop(_event, state2) {
  if (!state2.draggedNode)
    return;
}

// src/index.ts
function dragover(e) {
  e.preventDefault();
}
function initParent(parent, getValues, setValues, config) {
  if (!isBrowser)
    return;
  document.addEventListener("dragover", dragover);
  if (!state.parents.has(parent)) {
    state.parents.add(parent);
    const nodesObserver = new MutationObserver(nodesMutated);
    nodesObserver.observe(parent, { childList: true });
    if (parent.parentNode) {
      parent.addEventListener("dragenter", dzDragEvent2);
      parent.addEventListener("dragover", dzDragEvent2);
      parent.addEventListener("dragleave", dzDragEvent2);
      parent.addEventListener("drop", dzDragEvent2);
      const parentObserver = new MutationObserver(parentMutated);
      parentObserver.observe(parent.parentNode, { childList: true });
      if (!(parent.parentNode instanceof HTMLElement))
        return;
      state.parentObservers.set(parent.parentNode, parentObserver);
    }
  }
  if (!config.setDraggable) {
    config.setDraggable = setDraggable;
  }
  if (!config.removeDraggable) {
    config.removeDraggable = removeDraggable;
  }
  const currentPlugins = state.parentData.get(parent)?.config?.plugins;
  currentPlugins?.forEach((plugin) => {
    if (plugin.tearDown) {
      plugin.tearDown(parent, config);
    }
  });
  state.parentData.set(parent, {
    getValues,
    setValues,
    config,
    enabledNodes: []
  });
  config.plugins?.forEach((plugin) => {
    plugin.setUp(parent, config);
  });
  remap(parent);
}
function parentMutated(mutationList) {
  for (const mutation of mutationList) {
    for (const removedNode of Array.from(mutation.removedNodes)) {
      if (!(removedNode instanceof HTMLElement))
        continue;
      const parentData = state.parentData.has(removedNode);
      if (!parentData)
        continue;
      state.parentData.delete(removedNode);
      let hasParent = false;
      for (const node of Array.from(mutation.target.childNodes)) {
        if (state.parents.has(node)) {
          state.parents.delete(removedNode);
          hasParent = true;
          break;
        }
      }
      if (!hasParent) {
        const parentObserver = state.parentObservers.get(
          mutation.target
        );
        if (parentObserver) {
          parentObserver.disconnect();
          state.parentObservers.delete(mutation.target);
        }
      }
    }
  }
}
function nodesMutated(mutationList) {
  const parentEl = mutationList[0].target;
  if (!(parentEl instanceof HTMLElement))
    return;
  remap(parentEl);
}
function remap(parent) {
  const parentData = state.parentData.get(parent);
  if (!parentData)
    return;
  const enabledNodes = [];
  const config = parentData.config;
  for (let x = 0; x < parent.children.length; x++) {
    const child = parent.children[x];
    if (!isNode(child, state))
      continue;
    if (config && config.draggable && !config.draggable(child) || config?.disabled) {
      if (state.removeDraggable)
        state.removeDraggable(child);
    } else if (parentData.config?.setDraggable) {
      parentData.config.setDraggable(child);
      enabledNodes.push(child);
    }
  }
  if (enabledNodes.length !== parentData.getValues(parent).length) {
    console.warn(
      "The number of enabled nodes does not match the number of values."
    );
    return;
  }
  const values = parentData.getValues(parent);
  for (let x = 0; x < enabledNodes.length; x++) {
    const child = enabledNodes[x];
    state.nodeData.set(child, {
      value: values[x],
      index: x,
      privateClasses: []
    });
    if (!state.draggedNode || !config)
      continue;
    let dropZoneClass;
    const hasSelections = state.selectedValues.includes(values[x]);
    if (!hasSelections && state.nodeData.get(state.draggedNode)?.value !== values[x])
      continue;
    const isTouch = !!state.touchedNode;
    if (isTouch) {
      dropZoneClass = hasSelections ? config?.touchSelectionDropZoneClass : config?.touchDropZoneClass;
    } else {
      dropZoneClass = hasSelections ? config?.selectionDropZoneClass : config?.dropZoneClass;
    }
    handleClass([child], dropZoneClass, state, false, true);
  }
  state.parentData.set(parent, { ...parentData, enabledNodes });
}
function removeDraggable(child) {
  if (child.getAttribute("draggable") === "true") {
    child.removeAttribute("draggable");
  }
  child.removeEventListener("dragstart", nodeDragEvent2);
  child.removeEventListener("dragenter", nodeDragEvent2);
  child.removeEventListener("dragend", nodeDragEvent2);
  child.removeEventListener("dragover", nodeDragEvent2);
  child.removeEventListener("touchstart", nodeTouchEvent2);
  child.removeEventListener("touchmove", nodeTouchEvent2);
  child.removeEventListener("touchend", nodeTouchEvent2);
}
function setDraggable(child) {
  child.setAttribute("draggable", "true");
  child.addEventListener("dragstart", nodeDragEvent2);
  child.addEventListener("dragenter", nodeDragEvent2);
  child.addEventListener("dragend", nodeDragEvent2);
  child.addEventListener("dragover", nodeDragEvent2);
  child.addEventListener("touchstart", nodeTouchEvent2, { passive: false });
  child.addEventListener("touchmove", nodeTouchEvent2, { passive: false });
  child.addEventListener("touchend", nodeTouchEvent2);
}
function nodeDragEvent2(e) {
  e.stopPropagation();
  const targetData = nodeDragTarget(e);
  if (!targetData)
    return;
  if (e.type === "dragstart") {
    state.lastParent = targetData.targetParent;
    state.draggedNode = targetData.targetNode;
    state.initialParent = targetData.targetParent;
    if (!state.draggedNode)
      return;
    state.draggedNodes = [state.draggedNode];
    state.initialParentValues = targetData.targetParentData.getValues(
      targetData.targetParent
    );
  }
  const transferData = dragTransfer();
  if (transferData === void 0)
    return;
  const eventData = {
    event: e,
    ...targetData,
    ...transferData
  };
  const draggedConfig = eventData.draggedParentData.config;
  switch (eventData.event.type) {
    case "dragstart":
      draggedConfig?.dragstart ? draggedConfig.dragstart(eventData, state, dragstart) : dragstart(eventData, state);
      break;
    case "dragover":
      eventData.event.preventDefault();
      if (eventData.targetNode === eventData.draggedNode) {
        state.lastValue = eventData.targetNodeData.value;
        return;
      }
      if (eventData.targetParent === eventData.lastParent) {
        handleSort(eventData, state);
      } else {
        const config = eventData.targetParentData.config;
        config?.transferReturn ? config.transferReturn(eventData, state, transferReturn) : transferReturn(eventData, state);
      }
      break;
    case "dragend":
      draggedConfig?.end ? draggedConfig.end(eventData, state, end) : end(eventData, state);
      break;
    default:
      break;
  }
}
function nodeTouchEvent2(e) {
  e.stopPropagation();
  if (state.touchMoving && e.type === "touchstart")
    return;
  if (!isNode(e.currentTarget, state))
    return;
  if (e.type === "touchstart") {
    state.draggedNode = e.currentTarget;
    const clone = e.currentTarget.cloneNode(true);
    if (!(clone instanceof HTMLElement))
      return;
    const parentData = state.parentData.get(e.currentTarget.parentNode);
    if (!parentData)
      return;
    const parentValues = parentData.getValues(e.currentTarget.parentNode);
    if (!parentValues)
      return;
    state.touchedNode = clone;
    state.draggedNodes = [state.draggedNode];
    state.lastParent = e.currentTarget.parentNode;
    state.initialParent = e.currentTarget.parentNode;
    state.initialParentValues = parentValues;
  }
  if (!state.touchedNode)
    return;
  const transferData = touchTransfer();
  if (transferData === void 0)
    return;
  const config = transferData.draggedParentData.config;
  const touchEvent = {
    event: e,
    ...transferData
  };
  const draggedConfig = touchEvent.draggedParentData.config;
  let touchTarget;
  switch (e.type) {
    case "touchstart":
      config?.touchstart ? config.touchstart(touchEvent, state, touchstart) : touchstart(touchEvent, state);
      break;
    case "touchmove":
      config?.touchmove ? config.touchmove(touchEvent, state, touchmove) : touchmove(touchEvent, state);
      if (config?.longTouch && !state.longTouch)
        return;
      touchTarget = getTouchTarget(e);
      if (!touchTarget)
        return;
      if ("targetNode" in touchTarget) {
        const event = {
          event: e,
          ...touchTarget,
          ...transferData
        };
        if (event.targetNode === event.draggedNode) {
          state.lastValue = event.targetNodeData.value;
          return;
        }
        if (event.targetParent === event.lastParent) {
          handleSort(event, state);
        }
      }
      break;
    case "touchend":
      state.touchMoving = false;
      draggedConfig?.end ? draggedConfig.end(touchEvent, state, end) : end(touchEvent, state);
      break;
  }
}
function dzDragEvent2(e) {
  e.stopPropagation();
  e.preventDefault();
  const targetData = dzDragTarget(e);
  if (!targetData)
    return;
  const transferData = dragTransfer();
  if (transferData === void 0)
    return;
  const event = {
    event: e,
    ...targetData,
    ...transferData
  };
  const dzConfig = event.targetParentData.dzConfig;
  switch (event.event.type) {
    case "dragover":
      if (event.targetParent === event.draggedParent && state.leftParent) {
        event.targetParentData.config?.transferReturn ? event.targetParentData.config.transferReturn(
          event,
          state,
          transferReturn
        ) : transferReturn(event, state);
        state.leftParent = false;
      }
      break;
    case "dragleave":
      event.event.preventDefault();
      if (!event.targetParent.contains(
        document.elementFromPoint(event.event.clientX, event.event.clientY)
      )) {
        event.targetParentData.config?.dragleave ? event.targetParentData.config.dragleave(event, state, dragleave) : dragleave(event, state);
        state.leftParent = true;
      } else if (state.lastParent !== event.targetParent && state.lastParent === event.draggedParent) {
      }
      break;
    case "drop":
      if (event.targetParent == event.draggedParent) {
        dzConfig && dzConfig.drop ? dzConfig.drop(event, state, drop) : drop(event, state);
      }
  }
}
function handleSort(event, state2) {
  const clientX = "touchedNode" in event ? event.event.touches[0].clientX : event.event.clientX;
  const clientY = "touchedNode" in event ? event.event.touches[0].clientY : event.event.clientY;
  if (state2.lastValue === event.targetNodeData.value) {
    if (state2.direction !== 1) {
      return;
    }
    if (clientY >= state2.lastCoordinates.y - 20 && clientX >= state2.lastCoordinates.x - event.targetNode.getBoundingClientRect().width / 20) {
      return;
    }
  }
  if (!validateSort(event)) {
    return;
  }
  state2.lastValue = event.targetNodeData.value;
  state2.lastCoordinates = { x: clientX, y: clientY };
  state2.direction = event.targetNodeData.index > event.draggedNodeData.index ? 1 : -1;
  event.targetParentData.config?.sort ? event.targetParentData.config.sort(event, state2, sort) : sort(event, state2);
}
export {
  initParent as default,
  nodeDragEvent2 as nodeDragEvent,
  setUpDropZone
};
//# sourceMappingURL=index.js.map